import { useEffect, useState } from 'react';

/**
 * True while Alt/Option is held *together with* the primary command modifier
 * (⌘ on Apple, Ctrl elsewhere) — the hold-to-reveal gesture for keyboard-
 * shortcut hints (see `SegmentedTabs` and `HotkeyHint`). Requiring the command
 * modifier means a bare Alt/Option press (Alt-Tab, typing an Option glyph)
 * never flashes the hints; they only appear once the user is actually forming
 * the shortcut chord. Listens on the document so the gesture works wherever
 * focus is, and resets on window blur because Alt-Tab away never delivers the
 * keyup.
 */
export function useAltKeyHeld(enabled = true): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!enabled) {
      // Also clears state stuck on from an enabled → disabled flip mid-hold.
      setHeld(false);
      return;
    }
    const hide = () => setHeld(false);
    // Recompute from the live modifier state on every key event, so the reveal
    // tracks the chord regardless of whether Alt or the command key is pressed
    // (or released) first.
    const sync = (e: KeyboardEvent) => setHeld(e.altKey && (e.metaKey || e.ctrlKey));
    document.addEventListener('keydown', sync);
    document.addEventListener('keyup', sync);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('keydown', sync);
      document.removeEventListener('keyup', sync);
      window.removeEventListener('blur', hide);
    };
  }, [enabled]);

  return held;
}
